import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "../components/ui/alert-dialog";
import {
  Mail,
  Loader2,
  Search,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  ShieldOff,
  Clock,
  Send,
  AlertTriangle,
  Plane,
} from "lucide-react";

const STATUS_CONFIG = {
  enviado: { label: "Enviado", icon: CheckCircle, color: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  fallido: { label: "Fallido", icon: XCircle, color: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  bloqueado: { label: "Bloqueado", icon: ShieldOff, color: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
};

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <Card data-testid={`stat-${label.toLowerCase()}`} className="border-0 shadow-sm">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2.5 rounded-lg ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold leading-none">{value}</p>
          <p className="text-xs text-muted-foreground mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function EmailLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ total: 0, enviado: 0, fallido: 0, bloqueado: 0 });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [moduleFilter, setModuleFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [resendingId, setResendingId] = useState(null);
  const [vacBackfilling, setVacBackfilling] = useState(false);
  const pageSize = 30;
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", pageSize);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (moduleFilter !== "all") params.append("module", moduleFilter);
      if (searchTerm) params.append("search", searchTerm);

      const res = await axios.get(`${API}/email-logs/monitor?${params}`, getAuthHeaders());
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setStats(res.data.stats || {});
      setModules(res.data.modules || []);
    } catch (err) {
      toast.error("Error al cargar los logs de correo");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, moduleFilter, searchTerm, getAuthHeaders]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleResend = async (log) => {
    if (!log?.id) return;
    try {
      setResendingId(log.id);
      const res = await axios.post(`${API}/email-logs/${log.id}/resend`, {}, getAuthHeaders());
      toast.success(res.data?.message || `Correo reenviado a ${log.recipient}`);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reenviar el correo");
    } finally {
      setResendingId(null);
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  const handleVacacionesBackfill = async () => {
    try {
      setVacBackfilling(true);
      const res = await axios.post(
        `${API}/vacaciones-inicio/scheduler/disparar-fecha`,
        { fecha: "2026-06-26", force_resend: true },
        getAuthHeaders()
      );
      const r = res.data?.resumen || {};
      if (r.sin_plantilla) {
        toast.warning("No hay plantilla de 'vacaciones_inicio' activa configurada.");
      } else {
        toast.success(
          `Avisos de vacaciones reenviados: ${r.enviadas || 0} enviado(s), ${r.encontradas || 0} solicitud(es) encontrada(s)${r.omitidas_min_dias ? `, ${r.omitidas_min_dias} omitidas por mínimo de días` : ""}${r.errores ? `, ${r.errores} error(es)` : ""}.`,
          { duration: 8000 }
        );
      }
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudieron reenviar los avisos de vacaciones");
    } finally {
      setVacBackfilling(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    return d.toLocaleDateString("es-DO", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const moduleLabel = (m) => {
    const map = { gestion_humana: "Gestión Humana", configuracion: "Configuración", mensajeria: "Mensajería", sistema: "Sistema" };
    return map[m] || (m ? m.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "General");
  };

  return (
    <div className="flex h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 overflow-auto" data-testid="email-logs-page">
        <div className="max-w-[1400px] mx-auto p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-600" />
                Monitor de Correos
              </h1>
              <p className="text-sm text-muted-foreground mt-1">Historial de correos electrónicos enviados por el sistema</p>
            </div>
            <Button data-testid="refresh-btn" variant="outline" size="sm" onClick={() => fetchLogs()} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Actualizar
            </Button>
          </div>

          {/* Acción puntual: reenviar avisos de inicio de vacaciones del 26-jun-2026 */}
          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  data-testid="vacaciones-backfill-btn"
                  variant="outline"
                  size="sm"
                  className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 gap-1.5"
                  disabled={vacBackfilling}
                >
                  {vacBackfilling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plane className="w-4 h-4" />}
                  Reenviar avisos de vacaciones (26 jun 2026)
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="vacaciones-backfill-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Reenviar avisos de inicio de vacaciones</AlertDialogTitle>
                  <AlertDialogDescription>
                    Se revisarán las solicitudes <strong>aprobadas</strong> cuyas vacaciones
                    iniciaron el <strong>26 de junio de 2026</strong> y que cumplen el mínimo de
                    días configurado, y se reenviará el correo de aviso a los destinatarios
                    correspondientes (incluso si ya se había registrado como enviado).
                    Esta acción es de uso puntual. ¿Deseas continuar?
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="vacaciones-backfill-cancel">Cancelar</AlertDialogCancel>
                  <AlertDialogAction data-testid="vacaciones-backfill-confirm" onClick={handleVacacionesBackfill}>
                    Sí, reenviar ahora
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Total" value={stats.total || 0} icon={Mail} color="bg-blue-50 text-blue-600" />
            <StatCard label="Enviados" value={stats.enviado || 0} icon={Send} color="bg-emerald-50 text-emerald-600" />
            <StatCard label="Fallidos" value={stats.fallido || 0} icon={AlertTriangle} color="bg-red-50 text-red-600" />
            <StatCard label="Bloqueados" value={stats.bloqueado || 0} icon={ShieldOff} color="bg-amber-50 text-amber-600" />
          </div>

          {/* Filters */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex flex-wrap gap-3 items-center">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    data-testid="search-input"
                    placeholder="Buscar por destinatario o asunto..."
                    className="pl-9"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                  />
                </div>
                <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                  <SelectTrigger data-testid="status-filter" className="w-[150px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="enviado">Enviado</SelectItem>
                    <SelectItem value="fallido">Fallido</SelectItem>
                    <SelectItem value="bloqueado">Bloqueado</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={moduleFilter} onValueChange={(v) => { setModuleFilter(v); setPage(0); }}>
                  <SelectTrigger data-testid="module-filter" className="w-[180px]">
                    <SelectValue placeholder="Módulo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los módulos</SelectItem>
                    {modules.map((m) => (
                      <SelectItem key={m} value={m}>{moduleLabel(m)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-0 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="email-logs-table">
                <thead>
                  <tr className="bg-slate-50 border-b">
                    <th className="text-left p-3 font-medium text-slate-600">Fecha</th>
                    <th className="text-left p-3 font-medium text-slate-600">Destinatario</th>
                    <th className="text-left p-3 font-medium text-slate-600">Asunto</th>
                    <th className="text-left p-3 font-medium text-slate-600">Módulo</th>
                    <th className="text-left p-3 font-medium text-slate-600">Evento</th>
                    <th className="text-center p-3 font-medium text-slate-600">Estado</th>
                    <th className="text-left p-3 font-medium text-slate-600">Error</th>
                    <th className="text-center p-3 font-medium text-slate-600">Reenviar</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></td></tr>
                  ) : logs.length === 0 ? (
                    <tr><td colSpan={8} className="text-center py-12 text-muted-foreground">No se encontraron registros</td></tr>
                  ) : logs.map((log, i) => {
                    const cfg = STATUS_CONFIG[log.status] || STATUS_CONFIG.fallido;
                    const Icon = cfg.icon;
                    return (
                      <tr key={log.id || i} data-testid={`log-row-${i}`} className="border-b last:border-0 hover:bg-slate-50/50 transition-colors">
                        <td className="p-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-slate-600">
                            <Clock className="w-3.5 h-3.5 text-slate-400" />
                            {formatDate(log.created_at)}
                          </div>
                        </td>
                        <td className="p-3">
                          <span className="font-medium text-slate-700">{log.recipient || "-"}</span>
                        </td>
                        <td className="p-3 max-w-[280px]">
                          <span className="truncate block text-slate-600" title={log.subject}>{log.subject || "-"}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-slate-500 text-xs">{moduleLabel(log.module)}</span>
                        </td>
                        <td className="p-3">
                          <span className="text-slate-500 text-xs">{log.event ? log.event.replace(/_/g, " ") : "-"}</span>
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline" className={`${cfg.color} gap-1 text-xs`}>
                            <Icon className="w-3 h-3" />
                            {cfg.label}
                          </Badge>
                        </td>
                        <td className="p-3 max-w-[200px]">
                          <span className="truncate block text-xs text-red-500" title={log.error}>{log.error || ""}</span>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            data-testid={`resend-btn-${i}`}
                            variant="outline"
                            size="sm"
                            className="h-8 gap-1.5 text-xs"
                            disabled={!log.has_content || resendingId === log.id}
                            title={log.has_content ? `Reenviar a ${log.recipient}` : "Este correo no tiene contenido guardado para reenviar"}
                            onClick={() => handleResend(log)}
                          >
                            {resendingId === log.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Send className="w-3.5 h-3.5" />
                            )}
                            Reenviar
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between p-3 border-t bg-slate-50/50">
                <p className="text-xs text-muted-foreground">
                  Mostrando {page * pageSize + 1}-{Math.min((page + 1) * pageSize, total)} de {total}
                </p>
                <div className="flex items-center gap-1">
                  <Button data-testid="prev-page" variant="ghost" size="icon" className="h-8 w-8" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-xs px-2 text-muted-foreground">{page + 1} / {totalPages}</span>
                  <Button data-testid="next-page" variant="ghost" size="icon" className="h-8 w-8" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
